import { env } from '@/config/env';
import { getSupabase } from '@/lib/supabase';
import { safeStorage } from '@/lib/storage';
import { buildAttributionContext, generateSessionKey, isValidSessionKey, SESSION_KEY_STORAGE } from './attribution';

/** Returns the persistent first-party session key (creates one on first visit). */
export function getAttributionSessionKey(): string | null {
  const existing = safeStorage.get(SESSION_KEY_STORAGE);
  if (isValidSessionKey(existing)) return existing;
  try {
    const key = generateSessionKey();
    safeStorage.set(SESSION_KEY_STORAGE, key);
    return safeStorage.get(SESSION_KEY_STORAGE) === key ? key : null;
  } catch {
    return null;
  }
}

async function send(eventType: 'visit' | 'page_view' | 'cta_click', language: 'ar' | 'en', pagePath?: string) {
  if (!env.attributionEnabled) return;
  const supabase = await getSupabase();
  const sessionKey = getAttributionSessionKey();
  if (!supabase || !sessionKey) return;
  const context = buildAttributionContext({
    url: new URL(window.location.href),
    referrer: eventType === 'visit' ? document.referrer : '',
    userAgent: navigator.userAgent,
    viewportWidth: window.innerWidth,
    language,
  });
  if (pagePath) context.page_path = pagePath.slice(0, 300);
  // Fire-and-forget: attribution must never block or break the experience.
  try {
    await supabase.rpc('track_marketing_event', { p_session_key: sessionKey, p_event_type: eventType, p_context: context });
  } catch {
    /* ignore */
  }
}

let visitSent = false;

export function trackVisit(language: 'ar' | 'en') {
  if (visitSent) return;
  visitSent = true;
  void send('visit', language);
}

export function trackPageView(language: 'ar' | 'en', path: string) {
  void send('page_view', language, path);
}

export function trackCta(language: 'ar' | 'en', path: string) {
  void send('cta_click', language, path);
}

/** Links the anonymous session to the signed-in customer (server records first-touch source). */
export async function linkAttributionSession() {
  if (!env.attributionEnabled) return;
  const supabase = await getSupabase();
  const sessionKey = getAttributionSessionKey();
  if (!supabase || !sessionKey) return;
  try {
    await supabase.rpc('link_marketing_session', { p_session_key: sessionKey });
  } catch {
    /* ignore */
  }
}
